/**
 * @module domain/match
 * @description Q1314 Tier 2 "Match me to a cause": pure scoring of causes
 * against a donor's three answers. Transparent on purpose: every match comes
 * with the reasons it scored, so the UI can say WHY ("Matches: Education,
 * Kingston"), never a black box.
 */
import { CATEGORY_LABELS, PARISH_LABELS } from "../config/taxonomy";
import type { Cause, CategoryId, ParishId } from "./types";

/** "Where": a parish, or anywhere in Jamaica. */
export type PlaceAnswer = ParishId | "anywhere";

/** "How": the kind of impact the donor wants. */
export type PaceAnswer = "now" | "lasting" | "either";

/** The donor's answers. */
export interface MatchAnswers {
    interests: CategoryId[];
    place: PlaceAnswer;
    pace: PaceAnswer;
}

/** A scored cause with human-readable reasons. */
export interface CauseMatch {
    cause: Cause;
    score: number;
    reasons: string[];
}

/** Score weights: interest matters most, then place, then pace. */
const WEIGHT = { interest: 3, place: 2, islandwide: 1, pace: 1 } as const;

/** @returns one cause's score and the reasons behind it. */
export function scoreCause(cause: Cause, answers: MatchAnswers): CauseMatch {
    let score = 0;
    const reasons: string[] = [];

    if (answers.interests.includes(cause.category)) {
        score += WEIGHT.interest;
        reasons.push(CATEGORY_LABELS[cause.category]);
    }
    if (answers.place !== "anywhere") {
        if (cause.parish === answers.place) {
            score += WEIGHT.place;
            reasons.push(PARISH_LABELS[cause.parish]);
        } else if (cause.parish === "islandwide") {
            // Islandwide work also reaches the donor's parish: partial credit, said honestly.
            score += WEIGHT.islandwide;
            reasons.push(`Islandwide (includes ${PARISH_LABELS[answers.place]})`);
        }
    }
    const urgentNow: boolean = cause.urgency !== "ongoing";
    if ((answers.pace === "now" && urgentNow) || (answers.pace === "lasting" && !urgentNow)) {
        score += WEIGHT.pace;
        reasons.push(urgentNow ? "Needed now" : "Lasting impact");
    }
    return { cause, score, reasons };
}

/**
 * @returns the best `limit` matches with a score above 0, best first;
 * ties broken by popularity so results are stable.
 */
export function matchCauses(causes: readonly Cause[], answers: MatchAnswers, limit: number = 3): CauseMatch[] {
    return causes
        .map((c: Cause) => scoreCause(c, answers))
        .filter((m: CauseMatch) => m.score > 0)
        .sort((a, b) => b.score - a.score || a.cause.popularityRank - b.cause.popularityRank)
        .slice(0, limit);
}
